<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <!-- On a phone the buttons wrap under the title; it needs room below it there, none beside them. -->
      <h3 class="mb-3 mb-md-0">Bordbookinger</h3>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="loadedAt" class="text-muted small">Oppdatert {{ loadedAt }}</span>
        <button type="button" class="bo_btn_secondary" :disabled="!locationId || refreshing" title="Hent bookinger som har kommet inn"
                @click="refresh">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="refreshing" class="me-1" />
          {{ refreshing ? 'Oppdaterer…' : 'Oppdater' }}
        </button>
        <a v-if="!isEmployee" :href="'/bookingSettings?locationId=' + locationId" class="bo_btn_secondary text-decoration-none">Innstillinger</a>
        <button type="button" class="bo_btn" :disabled="!locationId" @click="openCreate">+ Ny bordbooking</button>
      </div>
    </div>

    <div v-if="hasMultiLocations()" class="select-location-box mt-3">
      <span class="ingredient-title">Velg avdeling</span>
      <select class="form-select" style="max-width:320px" v-model="locationId">
        <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
      </select>
    </div>

    <div v-if="location && !bookingEnabled" class="alert alert-warning mt-3 mb-0">
      Bordbooking på nett er ikke slått på for denne avdelingen.
      <template v-if="!isEmployee"><a :href="'/bookingSettings?locationId=' + locationId">Slå på under Innstillinger</a>. </template>
      <template v-else>En administrator kan slå det på under Innstillinger. </template>Dere kan fortsatt legge inn bookinger her.
    </div>

    <div class="d-flex flex-wrap align-items-center gap-2 mt-3">
      <button v-for="preset in presets" :key="preset.key" type="button"
              :class="activePreset === preset.key ? 'bo_btn' : 'bo_btn_secondary'" @click="applyPreset(preset)">{{ preset.label }}</button>
    </div>
    <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
      <span class="text-muted small">Fra</span>
      <datepicker v-model="fromDateObj" :locale="nbLocale" inputFormat="dd.MM.yyyy" class="form-control" style="max-width:140px"/>
      <span class="text-muted small">til</span>
      <datepicker v-model="toDateObj" :locale="nbLocale" inputFormat="dd.MM.yyyy" :lowerLimit="fromDateObj" class="form-control" style="max-width:140px"/>
      <!-- The project's own switch (a 46×26 px toggle), not a bare checkbox: easy to hit on a phone. -->
      <div class="form-switch-wrapper ms-2">
        <label class="form-switch">
          <div class="form-switch-container">
            <input type="checkbox" v-model="showCancelled" />
            <i></i>
            <span class="ms-2">Vis avbestilte</span>
          </div>
        </label>
      </div>
    </div>

    <div v-if="pendingCount > 0" class="alert alert-info mt-3 mb-0">
      {{ pendingCount }} {{ pendingCount === 1 ? 'booking venter' : 'bookinger venter' }} på bekreftelse. Gjesten får SMS når dere bekrefter.
    </div>

    <div v-if="!loading && days.length === 0" class="mt-4 text-muted">Ingen bordbookinger i perioden.</div>

    <div v-for="day in days" :key="day.date" class="mt-4">
      <div class="d-flex align-items-baseline gap-3 flex-wrap">
        <h5 class="mb-0 text-capitalize">{{ dayHeading(day.date) }}</h5>
        <span class="text-muted small">{{ day.active.length }} {{ day.active.length === 1 ? 'booking' : 'bookinger' }} · {{ day.guests }} {{ day.guests === 1 ? 'gjest' : 'gjester' }}</span>
      </div>
      <!-- Phone: one card per booking. A nine-column table scrolls sideways there, with the status and
           the two actions off-screen — the part staff need most. -->
      <div class="d-md-none mt-2">
        <div v-for="b in day.rows" :key="'card-' + b.id" :class="['bo_booking-card', { 'bo_booking-card--cancelled': b.status === 'CANCELLED' }]">
          <div class="d-flex align-items-center gap-2">
            <span class="bo_booking-card-time">{{ b.time }}</span>
            <span>{{ b.guests }} {{ b.guests === 1 ? 'gjest' : 'gjester' }}</span>
            <span class="ms-auto">
              <span :class="'badge ' + statusClass(b.status)">{{ statusText(b.status) }}</span>
              <span v-if="b.source === 'STAFF'" class="badge bg-secondary ms-1">Manuell</span>
            </span>
          </div>
          <div class="mt-1 fw-bold">{{ b.name }}</div>
          <div class="small">
            <a :href="'tel:' + b.phone">{{ b.phone }}</a>
            <span v-if="b.email"> · <a :href="'mailto:' + b.email">{{ b.email }}</a></span>
          </div>
          <div v-if="b.comment" class="small fst-italic mt-1">«{{ b.comment }}»</div>
          <div v-if="b.status !== 'CANCELLED'" class="d-flex justify-content-between mt-2">
            <button type="button" class="bo_btn_secondary" @click="cancelBooking(b)">Avbestill</button>
            <button v-if="b.status === 'PENDING'" type="button" class="bo_btn_success" @click="confirmBooking(b)">Bekreft</button>
          </div>
        </div>
      </div>

      <div class="table-responsive-sm mt-2 d-none d-md-block">
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th></th>
              <th>Tid</th>
              <th>Gjester</th>
              <th>Navn</th>
              <th>Telefon</th>
              <th class="d-none d-md-table-cell">E-post</th>
              <th>Kommentar</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in day.rows" :key="b.id" :class="{ 'bo_booking-cancelled': b.status === 'CANCELLED' }">
              <td>
                <a v-if="b.status !== 'CANCELLED'" class="remove-user-btn" @click="cancelBooking(b)">
                  <img src="@/assets/images/remove.png" width="21" title="Avbestill"/>
                </a>
              </td>
              <td class="fw-bold">{{ b.time }}</td>
              <td>{{ b.guests }}</td>
              <td>{{ b.name }}</td>
              <td><a :href="'tel:' + b.phone">{{ b.phone }}</a></td>
              <td class="d-none d-md-table-cell"><a v-if="b.email" :href="'mailto:' + b.email">{{ b.email }}</a></td>
              <td>{{ b.comment }}</td>
              <td>
                <span :class="'badge ' + statusClass(b.status)">{{ statusText(b.status) }}</span>
                <span v-if="b.source === 'STAFF'" class="badge bg-secondary ms-1" title="Lagt inn av dere">Manuell</span>
              </td>
              <td class="text-end">
                <button v-if="b.status === 'PENDING'" type="button" class="bo_btn_success" @click="confirmBooking(b)">Bekreft</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <BookingCreateModal ref="createModal" @created="load" />
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import BookingCreateModal from "@/components/booking/BookingCreateModal";
import BookingService from "@/services/booking/BookingService";
import UserUtil from "@/util/UserUtil";
import TextUtil from "@/util/TextUtil";
import LocationService from "@/services/location/LocationService";
import LocationUtil from "@/util/LocationUtil";
import moment from "moment/min/moment-with-locales";
import Datepicker from '@/components/other/ToggleDatepicker.vue';
import { nb } from 'date-fns/locale';

const iso = (m) => m.format('YYYY-MM-DD');

export default {
  name: 'Bookings',
  components: {LoadingSpinner, ConfirmDialogue, BookingCreateModal, Datepicker},
  data() {
    return {
      loading: true,
      refreshing: false,
      loadedAt: '',   // HH:mm of the last list fetched, beside «Oppdater»
      locationId: '',
      locations: [],
      bookings: [],
      fromDate: iso(moment()),
      toDate: iso(moment().add(30, 'days')),
      activePreset: 'upcoming',
      showCancelled: false,
      nbLocale: nb,
      presets: [
        { key: 'today', label: 'I dag', from: () => moment(), to: () => moment() },
        { key: 'tomorrow', label: 'I morgen', from: () => moment().add(1, 'day'), to: () => moment().add(1, 'day') },
        { key: 'week', label: 'Denne uken', from: () => moment().startOf('isoWeek'), to: () => moment().endOf('isoWeek') },
        { key: 'upcoming', label: 'Kommende 30 dager', from: () => moment(), to: () => moment().add(30, 'days') }
      ]
    };
  },
  computed: {
    /*
     * Booking settings are an administrator's to change, so an EMPLOYEE is not offered the way in. The
     * route has always refused them (employeeRestrictedPaths in router.js) — this stops the button from
     * being a link to a redirect. The endpoints are guarded in CagistaServices too: neither gate is the
     * one that counts on its own.
     */
    isEmployee() {
      return UserUtil.isEmployee();
    },
    // The pickers speak Date, the API speaks yyyy-MM-dd; the strings stay the source of truth.
    fromDateObj: {
      get() { return moment(this.fromDate, 'YYYY-MM-DD').toDate(); },
      set(d) { this.fromDate = iso(moment(d)); this.activePreset = ''; }
    },
    toDateObj: {
      get() { return moment(this.toDate, 'YYYY-MM-DD').toDate(); },
      set(d) { this.toDate = iso(moment(d)); this.activePreset = ''; }
    },
    location() {
      return this.locations.find(l => l.id === this.locationId) || null;
    },
    bookingEnabled() {
      return !!(this.location && this.location.tableBooking && this.location.tableBooking.enabled);
    },
    slotMinutes() {
      return (this.location && this.location.tableBooking && this.location.tableBooking.slotMinutes) || 30;
    },
    /**
     * The guest channels the location has switched on. The confirm and cancel dialogs used to promise SMS
     * flat out, which is a lie to a merchant who switched SMS off to avoid the kr 1 per message.
     *
     * Absent settings read as E-MAIL ONLY, matching the server's defaults in
     * TableBookingSettings.clamp — SMS is opted into, never inherited. These two defaults have to move
     * together: if this one says SMS the dialog promises a message the server will not send.
     */
    guestNotify() {
      const tb = (this.location && this.location.tableBooking) || {};
      return { sms: tb.guestSms === true, email: tb.guestEmail !== false };
    },
    pendingCount() {
      return this.bookings.filter(b => b.status === 'PENDING').length;
    },
    /** Bookings grouped per day, each day with its active (seat-holding) rows and guest total. */
    days() {
      const byDate = {};
      for (const b of this.bookings) {
        if (!this.showCancelled && b.status === 'CANCELLED') continue;
        if (!byDate[b.date]) byDate[b.date] = { date: b.date, rows: [], active: [], guests: 0 };
        byDate[b.date].rows.push(b);
        if (b.status !== 'CANCELLED') {
          byDate[b.date].active.push(b);
          byDate[b.date].guests += b.guests;
        }
      }
      return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    }
  },
  watch: {
    locationId() { this.load(); },
    fromDate() { this.load(); },
    toDate() { this.load(); }
  },
  mounted() {
    LocationService.listLocations().then(
        (response) => {
          this.locations = response.data;
          this.locationId = this.$route.query.locationId || (this.locations.length ? this.locations[0].id : '');
          if (!this.locationId) this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
        }
    );
  },
  methods: {
    hasMultiLocations() {
      return LocationUtil.hasMultiLocations();
    },
    applyPreset(preset) {
      this.activePreset = preset.key;
      this.fromDate = iso(preset.from());
      this.toDate = iso(preset.to());
    },
    /** The bookings of the period; quiet (from «Oppdater») keeps the list on screen and turns the button's icon instead. */
    load(quiet) {
      if (!this.locationId || !this.fromDate || !this.toDate) return;
      if (quiet === true) {
        this.refreshing = true;
      } else {
        this.loading = true;
      }
      BookingService.list(this.locationId, this.fromDate, this.toDate).then(
          (response) => {
            this.bookings = response.data;
            this.loadedAt = moment().format('HH:mm');
            this.loading = false;
            this.refreshing = false;
          },
          (error) => {
            console.log(error);
            this.loading = false;
            this.refreshing = false;
          }
      );
    },
    /**
     * Fetch the list again, for bookings guests have made on the web since the page was opened — staff keep
     * it open through a service. A preset follows the clock: a page left open since yesterday on «I dag»
     * moves to today rather than fetching yesterday again (the date watchers then load it).
     */
    refresh() {
      const preset = this.presets.find(p => p.key === this.activePreset);
      if (preset && (iso(preset.from()) !== this.fromDate || iso(preset.to()) !== this.toDate)) {
        this.applyPreset(preset);
        return;
      }
      this.load(true);
    },
    dayHeading(date) {
      const m = moment(date);
      const label = m.locale('nb').format('dddd D. MMMM');
      if (m.isSame(moment(), 'day')) return 'I dag – ' + label;
      if (m.isSame(moment().add(1, 'day'), 'day')) return 'I morgen – ' + label;
      return label;
    },
    statusText(status) {
      return { PENDING: 'Venter', CONFIRMED: 'Bekreftet', CANCELLED: 'Avbestilt' }[status] || status;
    },
    statusClass(status) {
      return { PENDING: 'bg-warning text-dark', CONFIRMED: 'bg-success', CANCELLED: 'bg-secondary' }[status] || 'bg-secondary';
    },
    /**
     * What this guest actually gets — «SMS og e-post», «SMS» or «e-post». E-mail only when the booking
     * has an address: staff may leave it out (2026-10-19), and the server never e-mails without one.
     */
    guestChannelsFor(b) {
      const sms = this.guestNotify.sms;
      const email = this.guestNotify.email && !!(b && b.email);
      if (sms && email) return 'SMS og e-post';
      if (sms) return 'SMS';
      if (email) return 'e-post';
      return '';
    },
    openCreate() {
      const date = this.activePreset === 'tomorrow' ? this.fromDate : iso(moment());
      this.$refs.createModal.open(this.locationId, this.slotMinutes, date, this.guestNotify);
    },
    async confirmBooking(b) {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Bekreft bordbooking',
        message: 'Bekreft bord for ' + TextUtil.escapeHtml(b.name) + ' ' + moment(b.date).locale('nb').format('D. MMMM') + ' kl. ' + b.time
            + ' (' + b.guests + (b.guests === 1 ? ' gjest' : ' gjester') + ')? '
            + (this.guestChannelsFor(b) ? 'Gjesten får bekreftelse på ' + this.guestChannelsFor(b) + '.' : 'Gjesten varsles ikke automatisk.'),
        okButton: 'Bekreft',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      BookingService.confirm(b.id).then(() => this.load(), (error) => console.log(error));
    },
    async cancelBooking(b) {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Avbestill bordbooking',
        message: 'Avbestill bookingen til ' + TextUtil.escapeHtml(b.name) + ' ' + moment(b.date).locale('nb').format('D. MMMM') + ' kl. ' + b.time
            + '? ' + (this.guestChannelsFor(b) ? 'Gjesten får ' + this.guestChannelsFor(b) + ' om at bookingen er avlyst.'
                : 'Gjesten varsles ikke automatisk.'),
        okButton: 'Avbestill bookingen',
        cancelButton: 'Avbryt',
        spreadButtons: true
      });
      if (!ok) return;
      BookingService.cancel(b.id, true).then(() => this.load(), (error) => console.log(error));
    }
  }
};
</script>

<style scoped>
.bo_booking-card {
  border: 1px solid var(--bo-line, #dee2e6);
  border-radius: 8px;
  padding: .75rem;
  margin-bottom: .5rem;
}
.bo_booking-card-time {
  font-weight: 700;
  font-size: 1.15rem;
}
.bo_booking-card--cancelled {
  color: #6c757d;
}
.bo_booking-card--cancelled .bo_booking-card-time,
.bo_booking-card--cancelled .fw-bold {
  text-decoration: line-through;
}
.bo_booking-cancelled td {
  color: #6c757d;
  text-decoration: line-through;
}
.bo_booking-cancelled td:last-child,
.bo_booking-cancelled td:first-child {
  text-decoration: none;
}
</style>
